import React, { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
// import { useMutation } from "@apollo/client";
import { toast } from "sonner";
import { assert, assertNotEmpty, assertValidEmail, assertValidUrl } from "@/utils/assertions";
import { sanitizeLength, sanitizeText, sanitizeUrl } from "@/utils/sanitization";
import { RateLimitPresets, checkRateLimit } from "@/utils/rateLimiter";
import { useOperationLock, useStateVersion } from "@/utils/stateManagement";
import { 
  commonSchemas, 
  emailSchema, 
  multilineItemsSchema, 
  optionalUrlSchema, 
  stringWithLength,
  tagsSchema,
  z 
} from "@/utils/zod-schemas";
import { 
  maxLength,
  minLength, 
  validateEmail,
  validateMultilineItems,
  validateSafeContent, 
  validateSafeUrl,
  validateTags
} from "@/utils/validation";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Code,
  FileText,
  Package,
  Shield,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import Navbar from "@/components/layout/navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
// import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Form constraints
const CONSTRAINTS = {
  name: { min: 3, max: 50 },
  description: { min: 50, max: 1000 },
  systemPrompt: { min: 100, max: 3000 },
  testInstructions: { min: 100, max: 2000 },
  securityConsiderations: { min: 50, max: 1000 },
  dataHandling: { min: 50, max: 1000 },
  capabilities: { min: 1, max: 20 },
  tags: { min: 1, max: 15 },
  permissions: { max: 10 },
} as const;

// Valid categories
const VALID_CATEGORIES = [
  'development', 'testing', 'security', 'data', 
  'automation', 'integration', 'other'
] as const;

// Valid pricing models
const VALID_PRICING_MODELS = [
  'free', 'freemium', 'subscription', 'usage', 'one-time'
] as const;

// Zod schema for the form
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const externalSAFormSchema = z.object({
  // Step 1: Basic Information
  name: stringWithLength(CONSTRAINTS.name.min, CONSTRAINTS.name.max, 'Name'),
  description: stringWithLength(CONSTRAINTS.description.min, CONSTRAINTS.description.max, 'Description'),
  category: z.enum(VALID_CATEGORIES),
  tags: tagsSchema(CONSTRAINTS.tags.min, CONSTRAINTS.tags.max),
  
  // Step 2: Technical Details
  repositoryUrl: commonSchemas.url,
  documentationUrl: optionalUrlSchema,
  capabilities: multilineItemsSchema(CONSTRAINTS.capabilities.min, CONSTRAINTS.capabilities.max),
  systemPrompt: stringWithLength(CONSTRAINTS.systemPrompt.min, CONSTRAINTS.systemPrompt.max, 'System prompt'),
  requiredPermissions: z.array(z.string()).max(CONSTRAINTS.permissions.max),
  
  // Step 3: Testing & Security
  testInstructions: stringWithLength(CONSTRAINTS.testInstructions.min, CONSTRAINTS.testInstructions.max, 'Test instructions'),
  securityConsiderations: stringWithLength(CONSTRAINTS.securityConsiderations.min, CONSTRAINTS.securityConsiderations.max, 'Security considerations'),
  dataHandling: stringWithLength(CONSTRAINTS.dataHandling.min, CONSTRAINTS.dataHandling.max, 'Data handling'),
  
  // Step 4: Business Information
  pricingModel: z.enum(VALID_PRICING_MODELS),
  supportEmail: emailSchema,
  termsAccepted: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the terms and conditions' }),
  }),
});

type ExternalSAFormData = z.infer<typeof externalSAFormSchema>;

const WIZARD_STEPS = [
  {
    title: "Basic Information",
    description: "Tell us about your specialized agent",
    icon: FileText,
  },
  {
    title: "Technical Details",
    description: "Provide implementation details",
    icon: Code,
  },
  {
    title: "Testing & Security",
    description: "Explain testing and security measures",
    icon: Shield,
  },
  {
    title: "Business Information",
    description: "Set up pricing and support",
    icon: Package,
  },
];

export function ExternalSASubmissionWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { acquireLock, isLocked } = useOperationLock();
  const { incrementVersion } = useStateVersion('wizard-form');
  const formRef = useRef<HTMLFormElement>(null);
  
  // Track component lifecycle
  const isMountedRef = useRef(true);
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm<ExternalSAFormData>({
    defaultValues: {
      requiredPermissions: [],
      pricingModel: "free",
    },
  });

  // TODO: Add mutation when GraphQL is ready
  // const [submitExternalAgent] = useMutation(SUBMIT_EXTERNAL_AGENT);

  const validateStepData = useCallback((step: number, data: Partial<ExternalSAFormData>): void => {
    // Additional runtime assertions for critical invariants
    // The form data has already been validated by Zod at this point
    switch (step) {
      case 0:
        assertNotEmpty(data.name, 'Agent name');
        assertNotEmpty(data.description, 'Description');
        assert(VALID_CATEGORIES.includes(data.category as typeof VALID_CATEGORIES[number]), 'Invalid category');
        assert(data.tags && data.tags.length > 0, 'At least one tag is required');
        break;
      case 1:
        assertValidUrl(data.repositoryUrl!, 'Repository URL');
        if (data.documentationUrl) {
          assertValidUrl(data.documentationUrl, 'Documentation URL');
        }
        assert(data.capabilities && data.capabilities.length > 0, 'At least one capability is required');
        assertNotEmpty(data.systemPrompt, 'System prompt');
        break;
      case 2:
        assertNotEmpty(data.testInstructions, 'Test instructions');
        assertNotEmpty(data.securityConsiderations, 'Security considerations');
        assertNotEmpty(data.dataHandling, 'Data handling');
        break;
      case 3:
        assert(VALID_PRICING_MODELS.includes(data.pricingModel as typeof VALID_PRICING_MODELS[number]), 'Invalid pricing model');
        assertValidEmail(data.supportEmail!, 'Support email');
        assert(data.termsAccepted === true, 'Terms must be accepted');
        break;
    }
  }, []);

  const validateStep = async () => {
    let fieldsToValidate: (keyof ExternalSAFormData)[] = [];
    
    switch (currentStep) {
      case 0:
        fieldsToValidate = ['name', 'description', 'category', 'tags'];
        break;
      case 1:
        fieldsToValidate = [
          'repositoryUrl',
          'documentationUrl',
          'capabilities',
          'systemPrompt',
        ];
        break;
      case 2:
        fieldsToValidate = [
          'testInstructions',
          'securityConsiderations',
          'dataHandling',
        ];
        break;
      case 3:
        fieldsToValidate = ['pricingModel', 'supportEmail', 'termsAccepted'];
        break;
    }
    
    const isValid = await trigger(fieldsToValidate);
    
    if (isValid) {
      // Additional validation
      try {
        const formData = watch();
        validateStepData(currentStep, formData);
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        }
        return false;
      }
    }
    
    return isValid;
  };

  const handleNext = async () => {
    // Prevent concurrent navigation
    if (isLocked('wizard-navigation')) return;
    
    await acquireLock('wizard-navigation', async () => {
      const isValid = await validateStep();
      if (isValid && currentStep < WIZARD_STEPS.length - 1) {
        incrementVersion(); // Track form state version
        setCurrentStep(currentStep + 1);
      }
    });
  };

  const handlePrevious = async () => {
    // Prevent concurrent navigation
    if (isLocked('wizard-navigation')) return;
    
    await acquireLock('wizard-navigation', async () => {
      if (currentStep > 0) {
        setCurrentStep(currentStep - 1);
      }
    });
  };

  const onSubmit = async (data: ExternalSAFormData) => {
    // Check rate limit
    const userId = 'current-user'; // TODO: Get from auth context
    if (!checkRateLimit(userId, 'submit-external-agent', RateLimitPresets.FORM_SUBMIT)) {
      toast.error('Too many submissions. Please wait a moment and try again.');
      return;
    }
    
    try {
      await acquireLock('submit-external-agent', async () => {
        setIsSubmitting(true);
        
        // Validate entire form
        for (let step = 0; step < WIZARD_STEPS.length; step++) {
          validateStepData(step, data);
        }
        
        // Sanitize all inputs
        const sanitizedData = { ...data };
        
        // Additional sanitization for specific fields
        sanitizedData.name = sanitizeLength(sanitizedData.name as string, CONSTRAINTS.name.max, 'Name');
        sanitizedData.description = sanitizeLength(sanitizedData.description as string, CONSTRAINTS.description.max, 'Description');
        sanitizedData.repositoryUrl = sanitizeUrl(sanitizedData.repositoryUrl as string);
        sanitizedData.documentationUrl = sanitizeUrl(sanitizedData.documentationUrl as string);
        sanitizedData.systemPrompt = sanitizeLength(sanitizedData.systemPrompt as string, CONSTRAINTS.systemPrompt.max, 'System prompt');
        sanitizedData.testInstructions = sanitizeLength(sanitizedData.testInstructions as string, CONSTRAINTS.testInstructions.max, 'Test instructions');
        sanitizedData.securityConsiderations = sanitizeLength(sanitizedData.securityConsiderations as string, CONSTRAINTS.securityConsiderations.max, 'Security considerations');
        sanitizedData.dataHandling = sanitizeLength(sanitizedData.dataHandling as string, CONSTRAINTS.dataHandling.max, 'Data handling');
        // Parse and validate tags (Zod schema already transforms to array)
        const tagArray = Array.isArray(sanitizedData.tags) 
          ? sanitizedData.tags
          : (sanitizedData.tags as string)
              .split(',')
              .map((tag: string) => sanitizeText(tag.trim()))
              .filter((tag: string) => tag.length > 0)
              .slice(0, CONSTRAINTS.tags.max);
        
        // Validate minimum tags
        assert(tagArray.length >= CONSTRAINTS.tags.min, 'At least one tag is required');
        
        // Parse and validate capabilities (Zod schema already transforms to array)
        const capabilitiesArray = Array.isArray(sanitizedData.capabilities)
          ? sanitizedData.capabilities
          : (sanitizedData.capabilities as string)
              .split('\n')
              .map((cap: string) => sanitizeText(cap.trim()))
              .filter((cap: string) => cap.length > 0)
              .slice(0, CONSTRAINTS.capabilities.max);
        
        assert(capabilitiesArray.length >= CONSTRAINTS.capabilities.min, 'At least one capability is required');
        
        // Validate permissions
        const permissions = sanitizedData.requiredPermissions as string[];
        assert(permissions.length <= CONSTRAINTS.permissions.max, `Maximum ${CONSTRAINTS.permissions.max} permissions allowed`);

        // TODO: Execute mutation when available
        /*
        const result = await submitExternalAgent({
          variables: {
            input: {
              name: sanitizedData.name,
              description: sanitizedData.description,
              category: sanitizedData.category,
              tags: tagArray,
              repositoryUrl: sanitizedData.repositoryUrl,
              documentationUrl: sanitizedData.documentationUrl,
              capabilities: capabilitiesArray,
              systemPrompt: sanitizedData.systemPrompt,
              requiredPermissions: permissions,
              testInstructions: sanitizedData.testInstructions,
              securityConsiderations: sanitizedData.securityConsiderations,
              dataHandling: sanitizedData.dataHandling,
              pricingModel: sanitizedData.pricingModel,
              supportEmail: sanitizedData.supportEmail,
            },
          },
        });
        */

        // Post-condition: Submission should be successful
        if (isMountedRef.current) {
          toast.success(
            "External SA submitted successfully! We'll review it within 2-3 business days."
          );
          navigate('/sa');
        }
      });
    } catch (error) {
      console.error('Error submitting external SA:', error);
      if (error instanceof Error) {
        toast.error(`Failed to submit: ${error.message}`);
      } else {
        toast.error('Failed to submit external SA. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const progressValue = useMemo(
    () => ((currentStep + 1) / WIZARD_STEPS.length) * 100,
    [currentStep]
  );

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container mx-auto max-w-4xl py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Submit External Specialized Agent</h1>
        <p className="mt-2 text-muted-foreground">
          Submit your specialized agent for review and listing on the marketplace
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <Progress value={progressValue} className="h-2" />
        <div className="mt-4 flex justify-between">
          {WIZARD_STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <div
                key={index}
                className={`flex flex-col items-center ${
                  isActive ? "text-primary" : isCompleted ? "text-primary/60" : "text-muted-foreground"
                }`}
              >
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isCompleted
                      ? "bg-primary/20"
                      : "bg-muted"
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
                <span className="mt-2 text-sm font-medium">{step.title}</span>
              </div>
            );
          })}
        </div>
      </div>

      <form ref={formRef} onSubmit={handleSubmit(onSubmit)} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>{WIZARD_STEPS[currentStep].title}</CardTitle>
            <CardDescription>
              {WIZARD_STEPS[currentStep].description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Basic Information */}
            {currentStep === 0 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Agent Name</Label>
                  <Input
                    id="name"
                    {...register("name", {
                      required: "Agent name is required",
                      minLength: minLength(CONSTRAINTS.name.min),
                      maxLength: maxLength(CONSTRAINTS.name.max),
                      validate: validateSafeContent,
                    })}
                    placeholder="e.g., Advanced Code Analyzer"
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    {...register("description", {
                      required: "Description is required",
                      minLength: minLength(CONSTRAINTS.description.min),
                      maxLength: maxLength(CONSTRAINTS.description.max),
                      validate: validateSafeContent,
                    })}
                    placeholder="Provide a detailed description of what your agent does..."
                    rows={6}
                  />
                  {errors.description && (
                    <p className="text-sm text-destructive">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    onValueChange={(value) => setValue("category", value as typeof VALID_CATEGORIES[number])}
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="development">Development</SelectItem>
                      <SelectItem value="testing">Testing & QA</SelectItem>
                      <SelectItem value="security">Security</SelectItem>
                      <SelectItem value="data">Data Analysis</SelectItem>
                      <SelectItem value="automation">Automation</SelectItem>
                      <SelectItem value="integration">Integration</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.category && (
                    <p className="text-sm text-destructive">
                      Category is required
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    {...register("tags", {
                      required: "At least one tag is required",
                      validate: (value) => {
                        if (typeof value === 'string') {
                          return validateTags(value, CONSTRAINTS.tags.min, CONSTRAINTS.tags.max);
                        }
                        // If already an array, validate length
                        return value.length >= CONSTRAINTS.tags.min && value.length <= CONSTRAINTS.tags.max
                          ? true
                          : `Must have between ${CONSTRAINTS.tags.min} and ${CONSTRAINTS.tags.max} tags`;
                      },
                    })}
                    placeholder="javascript, testing, automation, ci/cd"
                  />
                  {errors.tags && (
                    <p className="text-sm text-destructive">{errors.tags.message}</p>
                  )}
                </div>
              </>
            )}

            {/* Step 2: Technical Details */}
            {currentStep === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="repositoryUrl">Repository URL</Label>
                  <Input
                    id="repositoryUrl"
                    {...register("repositoryUrl", {
                      required: "Repository URL is required",
                      validate: validateSafeUrl,
                    })}
                    placeholder="https://github.com/yourusername/your-agent"
                  />
                  {errors.repositoryUrl && (
                    <p className="text-sm text-destructive">
                      {errors.repositoryUrl.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="documentationUrl">Documentation URL</Label>
                  <Input
                    id="documentationUrl"
                    {...register("documentationUrl", {
                      required: "Documentation URL is required",
                      validate: validateSafeUrl,
                    })}
                    placeholder="https://docs.your-agent.com"
                  />
                  {errors.documentationUrl && (
                    <p className="text-sm text-destructive">
                      {errors.documentationUrl.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="capabilities">
                    Capabilities (one per line)
                  </Label>
                  <Textarea
                    id="capabilities"
                    {...register("capabilities", {
                      required: "At least one capability is required",
                      validate: (value) => {
                        if (typeof value === 'string') {
                          return validateMultilineItems(value, CONSTRAINTS.capabilities.min, CONSTRAINTS.capabilities.max);
                        }
                        // If already an array, validate length
                        return value.length >= CONSTRAINTS.capabilities.min && value.length <= CONSTRAINTS.capabilities.max
                          ? true
                          : `Must have between ${CONSTRAINTS.capabilities.min} and ${CONSTRAINTS.capabilities.max} items`;
                      },
                    })}
                    placeholder="Code analysis&#10;Vulnerability detection&#10;Performance optimization&#10;Automated refactoring"
                    rows={4}
                  />
                  {errors.capabilities && (
                    <p className="text-sm text-destructive">
                      {errors.capabilities.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="systemPrompt">System Prompt</Label>
                  <Textarea
                    id="systemPrompt"
                    {...register("systemPrompt", {
                      required: "System prompt is required",
                      minLength: minLength(CONSTRAINTS.systemPrompt.min),
                      maxLength: maxLength(CONSTRAINTS.systemPrompt.max),
                      validate: validateSafeContent,
                    })}
                    placeholder="Define your agent's behavior, instructions, and constraints..."
                    rows={8}
                  />
                  {errors.systemPrompt && (
                    <p className="text-sm text-destructive">
                      {errors.systemPrompt.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Required Permissions</Label>
                  <div className="space-y-2">
                    {[
                      { value: "read_code", label: "Read source code" },
                      { value: "write_code", label: "Write/modify code" },
                      { value: "execute_commands", label: "Execute commands" },
                      { value: "network_access", label: "Network access" },
                      { value: "file_system", label: "File system access" },
                    ].map((permission) => (
                      <div key={permission.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={permission.value}
                          onCheckedChange={(checked) => {
                            const current = watch("requiredPermissions") || [];
                            if (checked) {
                              setValue("requiredPermissions", [...current, permission.value]);
                            } else {
                              setValue(
                                "requiredPermissions",
                                current.filter((p) => p !== permission.value)
                              );
                            }
                          }}
                        />
                        <Label
                          htmlFor={permission.value}
                          className="text-sm font-normal"
                        >
                          {permission.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Testing & Security */}
            {currentStep === 2 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="testInstructions">Testing Instructions</Label>
                  <Textarea
                    id="testInstructions"
                    {...register("testInstructions", {
                      required: "Testing instructions are required",
                      minLength: minLength(CONSTRAINTS.testInstructions.min, "Please provide detailed testing instructions (min 100 characters)"),
                      maxLength: maxLength(CONSTRAINTS.testInstructions.max),
                      validate: validateSafeContent,
                    })}
                    placeholder="Explain how to test your agent, including setup steps, test scenarios, and expected outcomes..."
                    rows={6}
                  />
                  {errors.testInstructions && (
                    <p className="text-sm text-destructive">
                      {errors.testInstructions.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="securityConsiderations">
                    Security Considerations
                  </Label>
                  <Textarea
                    id="securityConsiderations"
                    {...register("securityConsiderations", {
                      required: "Security considerations are required",
                      minLength: minLength(CONSTRAINTS.securityConsiderations.min, "Please provide security details (min 50 characters)"),
                      maxLength: maxLength(CONSTRAINTS.securityConsiderations.max),
                      validate: validateSafeContent,
                    })}
                    placeholder="Describe security measures, potential risks, and mitigation strategies..."
                    rows={4}
                  />
                  {errors.securityConsiderations && (
                    <p className="text-sm text-destructive">
                      {errors.securityConsiderations.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dataHandling">Data Handling</Label>
                  <Textarea
                    id="dataHandling"
                    {...register("dataHandling", {
                      required: "Data handling information is required",
                      minLength: minLength(CONSTRAINTS.dataHandling.min, "Please explain data handling (min 50 characters)"),
                      maxLength: maxLength(CONSTRAINTS.dataHandling.max),
                      validate: validateSafeContent,
                    })}
                    placeholder="Explain how your agent handles user data, what data is collected, stored, or transmitted..."
                    rows={4}
                  />
                  {errors.dataHandling && (
                    <p className="text-sm text-destructive">
                      {errors.dataHandling.message}
                    </p>
                  )}
                </div>

                <div className="rounded-lg bg-amber-50 dark:bg-amber-950 p-4">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                    Security Review Process
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-amber-800 dark:text-amber-200">
                    <li>• All agents undergo security review before approval</li>
                    <li>• Automated vulnerability scanning</li>
                    <li>• Manual code review for critical permissions</li>
                    <li>• Ongoing monitoring after deployment</li>
                  </ul>
                </div>
              </>
            )}

            {/* Step 4: Business Information */}
            {currentStep === 3 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="pricingModel">Pricing Model</Label>
                  <Select
                    defaultValue="free"
                    onValueChange={(value) => setValue("pricingModel", value as typeof VALID_PRICING_MODELS[number])}
                  >
                    <SelectTrigger id="pricingModel">
                      <SelectValue placeholder="Select pricing model" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="freemium">
                        Freemium (Basic free, premium features paid)
                      </SelectItem>
                      <SelectItem value="subscription">
                        Subscription-based
                      </SelectItem>
                      <SelectItem value="usage">Usage-based</SelectItem>
                      <SelectItem value="one-time">One-time purchase</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="supportEmail">Support Email</Label>
                  <Input
                    id="supportEmail"
                    type="email"
                    {...register("supportEmail", {
                      required: "Support email is required",
                      validate: validateEmail,
                    })}
                    placeholder="support@your-domain.com"
                  />
                  {errors.supportEmail && (
                    <p className="text-sm text-destructive">
                      {errors.supportEmail.message}
                    </p>
                  )}
                </div>

                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="revenue-share">
                    <AccordionTrigger>Revenue Sharing</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Our standard revenue share for external agents:
                        </p>
                        <div className="rounded-lg border p-4">
                          <div className="flex justify-between">
                            <span className="font-medium">Developer Share</span>
                            <span className="font-bold text-green-600">70%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium">Platform Fee</span>
                            <span>30%</span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          * Premium support packages available for reduced platform fees
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="termsAccepted"
                      onCheckedChange={(checked) => {
                        if (checked === true) {
                          setValue("termsAccepted", true);
                        }
                      }}
                    />
                    <div className="space-y-1">
                      <Label
                        htmlFor="termsAccepted"
                        className="text-sm font-normal"
                      >
                        I agree to the A2A Platform{" "}
                        <a href="/terms" className="text-primary underline">
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a href="/developer-agreement" className="text-primary underline">
                          Developer Agreement
                        </a>
                      </Label>
                      {errors.termsAccepted && (
                        <p className="text-sm text-destructive">
                          You must accept the terms to proceed
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-4">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    What happens next?
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-blue-800 dark:text-blue-200">
                    <li>• Your agent will be reviewed within 2-3 business days</li>
                    <li>• You'll receive email updates on the review status</li>
                    <li>• Once approved, your agent will be listed on the marketplace</li>
                    <li>• You can track performance in the Developer Analytics Portal</li>
                  </ul>
                </div>
              </>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>
            {currentStep < WIZARD_STEPS.length - 1 ? (
              <Button type="button" onClick={handleNext}>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit for Review"}
              </Button>
            )}
          </CardFooter>
        </Card>
      </form>
      </div>
    </div>
  );
}